'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import type { AlternativeLanguage } from '../../types/AlternativeLanguage';
import type { Navigation } from '../../types/Navigation';
import HamburgerButton from '../atoms/HamburgerButton';
import NavItem from '../atoms/NavItem';
import LanguageSwitcher from '../LanguageSwitcher';

interface NavbarProps {
    navigation: Navigation;
    locale: string;
    alternativeLanguages?: AlternativeLanguage[];
}

export default function Navbar({ navigation, locale, alternativeLanguages }: NavbarProps) {
    const pathname = usePathname();
    const { primary } = navigation;
    const [showBackground, setShowBackground] = useState(false);
    const [hasMounted, setHasMounted] = useState(false);

    const isActive = (path: string) => pathname === path;

    // Mark component as mounted to enable client-side features
    useEffect(() => {
        setHasMounted(true);
    }, []);

    // Add resize event listener
    useEffect(() => {
        if (!hasMounted) return;

        const handleResize = () => {
            const primaryMenu = document.getElementById('primary-menu');
            const menuItems = primaryMenu?.querySelectorAll('li') as NodeListOf<HTMLLIElement>;

            const menuVisible = primaryMenu?.scrollWidth
                ? primaryMenu?.clientWidth >= primaryMenu?.scrollWidth
                : false;
            setShowBackground(menuVisible);

            if (menuVisible) {
                primaryMenu?.classList.add('visible');
                // Stagger animation for menu items
                // Convert NodeList to Array to use for...of
                const menuItemsArray = menuItems ? Array.from(menuItems) : [];

                // Use for...of with index tracking for staggered animation
                let index = 0;
                for (const item of menuItemsArray) {
                    setTimeout(() => {
                        item.classList.add('visible');
                    }, 50 * index);
                    index++;
                }
            } else {
                primaryMenu?.classList.remove('visible');
                // Convert NodeList to Array to use for...of
                const menuItemsArray = menuItems ? Array.from(menuItems) : [];

                for (const item of menuItemsArray) {
                    item.classList.remove('visible');
                }
            }
        };

        // Check initial window width
        const isLargeScreen = window.innerWidth >= 1024;
        setShowBackground(isLargeScreen);

        // Run once on mount
        setTimeout(handleResize, 100);

        // Debounce resize event
        let resizeTimer: NodeJS.Timeout;
        const debouncedResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(handleResize, 100);
        };

        window.addEventListener('resize', debouncedResize);

        return () => {
            clearTimeout(resizeTimer);
            window.removeEventListener('resize', debouncedResize);
        };
    }, [hasMounted]);

    return (
        <nav className="header-navigation absolute top-0 z-10 w-full sm:fixed">
            <style jsx global>{`
                /* Base styles for the menu */
                #primary-menu {
                    display: none;
                    transition: opacity 0.3s ease, max-height 0.3s ease;
                    opacity: 0;
                    max-height: 0;
                    overflow: hidden;
                    pointer-events: none;
                }
                
                /* Visible state */
                #primary-menu.visible {
                    display: flex;
                    opacity: 1;
                    max-height: 100px;
                    pointer-events: auto;
                }
                
                /* Menu item animations */
                #primary-menu li {
                    opacity: 0;
                    transform: translateY(-8px);
                    transition: opacity 0.2s ease, transform 0.2s ease;
                }
                
                #primary-menu li.visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                /* Container background transitions */
                .navbar-outer-container {
                    transition: background-color 0.3s ease;
                }
                
                /* Always show on larger screens */
                @media (min-width: 768px) {
                    #primary-menu {
                        display: flex;
                    }
                }
            `}</style>

            <div
                className={cn(
                    'navbar-outer-container flex text-haug-coral-100 pl-navbar-start',
                    showBackground ? 'sm:shadow-xl md:bg-haug-ivory-100' : '',
                    'md:text-haug-choco-100 lg:mr-8 lg:ml-navbar-start lg:pl-0'
                )}
            >
                <div className="navbar-container flex h-12 w-full justify-end overflow-y-hidden">
                    <ul id="primary-menu" className="flex h-12 flex-grow space-x-0 lg:pl-8">
                        {primary?.map((item) => (
                            <NavItem
                                key={item.url}
                                title={item.title}
                                url={item.url}
                                active={isActive(item.url)}
                            />
                        ))}
                    </ul>

                    <div
                        className={`flex h-12 items-center ${showBackground ? 'bg-haug-ivory-100' : ''}`}
                    >
                        {alternativeLanguages && (
                            <LanguageSwitcher
                                locale={locale}
                                alternativeLanguages={alternativeLanguages}
                                variant="navbar"
                                className="language-switcher"
                            />
                        )}

                        <HamburgerButton />
                    </div>
                </div>
            </div>
        </nav>
    );
}
